// Exported so the wizard acceptance payload can record the exact revision
// a partner accepted. Bump whenever the content of this page materially changes,
// and add an entry to the Revision history section at the end.
//
// 1.1: METER.P0 PR-A2 (#2987) and BILL.H3 (#3138), AVR rulings 6157615295,
// 6253013136 and 6283011367 (billing timing, annual prepayment, amendment clause),
// and 6333589558, 6333837266 and 6333869164 (22 Oct: billing dates, notice,
// updates, switching, termination, the contracting entity). Still 1.1: none of
// it was live before this version.
// 6354910710 (23 Oct: auto-renewal, the breach refund, a suspension clause, leaving
// before the first billing date) and, on #3773, 6453941362, 6453999798 and 6484434044
// (28-29 Oct: suspension, deemed receipt both ways, a defined Material Update, each
// change keeping its own update-exit deadline, and the drafting fixes).
// 6490304401 (29 Oct, on the targeted R2: acceptance by continued use part by part, a Material
// Update that is any change adverse to the Partner, notice to the last administrator, the
// suspension credit, and the move from 1.0 to 1.1 as a Material Update for a Partner active
// under 1.0).
// 6509100852 (30 Oct, AVR's ruling; his 30 Oct answer to Navya's question 6512533194 is recorded
// in 6515323823): a Material Update that the notice does not list applies only from a later
// notice that lists it, which is then its notice of update, and the update-exit deadline runs
// from it.
// Existing 1.0 acceptances are left as they are; re-acceptance is not required.
export const PARTNER_TERMS_VERSION = '1.1';
export const PARTNER_TERMS_EFFECTIVE_DATE = 'October 2026';

const sectionStyle: React.CSSProperties = {
  marginBottom: 24,
};

const h2Style: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--text)',
  marginTop: 28,
  marginBottom: 10,
};

const pStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  lineHeight: 1.65,
  color: 'var(--text-secondary)',
  margin: '0 0 10px 0',
};

const ulStyle: React.CSSProperties = {
  ...pStyle,
  paddingLeft: 20,
  margin: '0 0 10px 0',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  marginTop: 8,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  background: 'var(--surface-alt)',
  borderBottom: '1px solid var(--border)',
  fontWeight: 600,
  color: 'var(--text)',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text-secondary)',
};

export default function PartnerTermsPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '48px 24px' }}>
      <div
        style={{
          maxWidth: 820,
          margin: '0 auto',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '40px 48px',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <header style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 28,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            PLATFORM_PARTNER Programme Agreement
          </h1>
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginTop: 6,
            }}
          >
            Version {PARTNER_TERMS_VERSION} &mdash; {PARTNER_TERMS_EFFECTIVE_DATE}
          </p>
        </header>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. Definitions</h2>
          <ul style={ulStyle}>
            <li>
              <strong>BluePrint</strong> &mdash; Arivaartham Labs Private Limited, operating the BluePrint service
              (blueprintsdlc.com).
            </li>
            <li><strong>Partner</strong> &mdash; the organisation enrolled in the PLATFORM_PARTNER programme.</li>
            <li>
              <strong>Partner&apos;s administrators</strong> &mdash; the users holding the tenant administrator role
              (TENANT_ADMIN) in the Partner&apos;s tenant on the Platform.
            </li>
            <li>
              <strong>notice</strong> &mdash;  an email. BluePrint gives notice to the Partner by email to each
              of the Partner&apos;s administrators at the addresses recorded on the Platform or, if the Partner
              has no administrator, to the email address of the user who completed the Partner&apos;s
              onboarding; the Partner gives notice to BluePrint by email to support@blueprintsdlc.com. A notice
              is deemed received 24 hours after it is sent or, for a notice to more than one address, 24 hours
              after it is sent to the last of them, and any notice period runs from deemed receipt.
            </li>
            <li><strong>Client</strong> &mdash; an end-customer organisation onboarded by the Partner onto the Platform.</li>
            <li><strong>Platform</strong> &mdash; the BluePrint SDLC software-as-a-service offering.</li>
            <li><strong>Fees</strong> &mdash; amounts payable to BluePrint under Section 3.</li>
            <li>
              <strong>Pooled Token Allowance</strong> &mdash; the monthly LLM token quota set under Section 3, shared across
              all of the Partner&apos;s Client Tenants.
            </li>
            <li>
              <strong>Effective Date</strong> &mdash; the moment, in UTC, at which the Partner completes onboarding in the
              onboarding wizard.
            </li>
            <li>
              <strong>billing date</strong> &mdash; 00:00 UTC on the 1st of each calendar month. The first billing date is
              the first one after the Effective Date, except for a Partner already active under version 1.0 when version
              1.1 applies to it: its first billing date is the first billing date on or after the day version 1.1 applies
              to it.
            </li>
            <li>
              <strong>monthly period</strong> &mdash; the period from one billing date to the next. An annual term is 12
              consecutive monthly periods, with the Active Client Tenants counted at the start of each.
            </li>
            <li>
              <strong>termination date</strong> &mdash; the date, in UTC, on which termination takes effect.
            </li>
            <li>
              <strong>Material Update</strong> &mdash;  an update to these terms that adversely affects the
              Partner, including a change to the Fees, the Pooled Token Allowance or the Partner&apos;s
              obligations that is adverse to the Partner, and a reduction of BluePrint&apos;s obligations or of
              the Partner&apos;s rights, such as its warranties, its limit of liability or its data terms. A
              change in the Partner&apos;s favour, such as a reduction of the Fees, and a change that only
              rewords these terms without changing their effect are not Material Updates.
            </li>
            <li>
              <strong>active under version 1.0</strong> &mdash;  a Partner that accepted version 1.0 and has not
              terminated.
            </li>
            <li><strong>Client Tenant</strong> &mdash; a logically isolated workspace belonging to a single Client.</li>
            <li>
              <strong>linked</strong> &mdash; a Client Tenant is linked when it is associated with the Partner&apos;s tenant on
              the Platform.
            </li>
            <li>
              <strong>Active Client Tenant</strong> &mdash; a Client Tenant linked at the billing date that starts a monthly
              period, counted for that whole monthly period. A Client Tenant linked after that
              counts from the next monthly period; one unlinked mid-period still counts for the current one.
            </li>
            <li><strong>AI actions</strong> &mdash; features of the Platform that call a language model.</li>
            <li><strong>White-Label Configuration</strong> &mdash; the Partner&apos;s custom platform branding applied to Client Tenants.</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. Grant of Licence</h2>
          <p style={pStyle}>
            Subject to the Partner&apos;s compliance with this agreement, BluePrint grants the Partner a non-exclusive,
            non-transferable, revocable licence to access the Platform and onboard an unlimited number of Client Tenants
            linked to the Partner&apos;s tenant. BluePrint may revoke the licence only on termination under Section 10, and
            suspend it only as Section 10 allows. The Partner may configure White-Label Configuration on Client Tenants.
          </p>
          <p style={pStyle}>
            The Partner may not: (a) resell the Platform to third parties outside the Partner programme framework,
            (b) reverse-engineer, decompile or otherwise attempt to derive source code, (c) use the Platform to build
            a competing product, or (d) remove or obscure the &ldquo;Powered by BluePrint&rdquo; attribution where required.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. Commercial Terms</h2>
          <p style={pStyle}>
            Fees are billed monthly, except that a Partner on annual prepayment is billed the base fee annually in
            advance, as set out below. Fees are consolidated at the Partner level, across all Client Tenants. The base fee
            is billed in advance: on each billing date or, on annual prepayment, on the first billing date of each annual
            term. The Active Client Tenants counted at each billing date set both that monthly
            period&apos;s Pooled Token Allowance and its per-client fee. The per-client fee for a monthly period
            is billed in arrears, on the next billing date, or on the termination date for the final monthly period
            (Section 10). The Partner is responsible for invoicing its own Clients under separate commercial arrangements.
          </p>
          <ul style={ulStyle}>
            <li>Base fee: <strong>$199 per month</strong>.</li>
            <li>Per-client fee: <strong>$39 per month</strong> per Active Client Tenant.</li>
            <li>
              With annual prepayment, the base fee is billed annually in advance at a <strong>20% discount</strong>, and
              per-client fees remain billed monthly in arrears at a <strong>20% discount</strong>.
            </li>
            <li>
              A Partner chooses annual prepayment, or switches to it from monthly billing, by notice to
              BluePrint. The change takes effect on the first billing date after the notice is deemed received,
              which starts the annual term: the base fee is then billed annually in advance, and the 20%
              per-client discount applies from that monthly period. A change from annual prepayment to monthly
              billing takes effect only at renewal, as set out below.
            </li>
            <li>
              An annual term renews for another 12 monthly periods, starting on the billing date after its last monthly
              period (the renewal billing date), unless the Partner switches to monthly billing by notice deemed received at
              least 30 days before the renewal billing date. An annual term chosen at sign-up starts on the first billing
              date, after the free part-month.
            </li>
            <li>BYOLLM discounts: approximately <strong>45% / 46% / 38%</strong> depending on the contracted tier.</li>
            <li>
              Pooled Token Allowance: <strong>20M tokens per month, plus 5M tokens per month for each Active Client
              Tenant</strong>, shared across all Client Tenants.
            </li>
            <li>
              When the Pooled Token Allowance is used up, AI actions pause for all Client Tenants until the next monthly
              period; everything else keeps working.
            </li>
            <li>
              The part-month before the first billing date is free: its Pooled Token Allowance is the base 20M tokens only,
              and no Fees apply. A Partner already active under version 1.0 when version 1.1 applies to it has no free
              part-month.
            </li>
            <li>Payment terms: <strong>net 30 days</strong> from invoice date.</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>4. Client Relationships</h2>
          <p style={pStyle}>
            The Partner holds the direct commercial relationship with its Clients. BluePrint does not invoice Clients
            directly and its name will not appear on Client invoices. The Partner is solely responsible for its own
            contracts, SLAs, and service commitments to Clients.
          </p>
          <p style={pStyle}>
            The <strong>&ldquo;Powered by BluePrint&rdquo;</strong> attribution badge must remain visible in the Client Tenant UI
            regardless of White-Label Configuration.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. Data Isolation and Sub-processors</h2>
          <p style={pStyle}>
            Each Client Tenant is logically isolated; Client data is not shared across tenants. BluePrint staff
            may access a Client Tenant only upon explicit request from the Partner or Client, and each
            such access is recorded in an audit log made available to the Partner.
          </p>
          <p style={pStyle}>
            Sub-processors include <strong>Anthropic</strong> (LLM inference), <strong>Amazon Web Services</strong>
            (hosting and storage), and <strong>Amazon SES</strong> (transactional email).
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. Intellectual Property</h2>
          <p style={pStyle}>
            BluePrint retains all rights, title and interest in the Platform, its agents, prompts, and underlying
            software. The Partner retains ownership of the domain packs it authors and of Client artefacts produced
            within its Client Tenants.
          </p>
          <p style={pStyle}>
            BluePrint does not train its foundation models on Client data.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Confidentiality</h2>
          <p style={pStyle}>
            Each party agrees to keep the other party&apos;s confidential information (including Client data and pricing
            terms) in confidence, and to use such information only for the purposes of this agreement. The obligations
            of this section survive termination for a period of three years.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Warranties</h2>
          <p style={pStyle}>
            BluePrint warrants that the Platform will perform materially in accordance with its published documentation.
            Outputs generated by AI agents are probabilistic in nature and require human review before reliance in any
            production or commercial context. The Partner accepts responsibility for reviewing AI-generated artefacts
            before delivering them to Clients.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Limitation of Liability</h2>
          <p style={pStyle}>
            To the maximum extent permitted by law, BluePrint&apos;s total liability under this agreement is capped at the
            Fees paid by the Partner in the three months preceding the event giving rise to liability. Neither party
            shall be liable for indirect, incidental, consequential, or punitive damages, lost profits, or loss of data.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>10. Term and Termination</h2>
          <p style={pStyle}>
            Either party may terminate this agreement for convenience by notice; termination takes effect at
            23:59:59 UTC on the 30th day after the notice is deemed received. Either party may terminate for
            material breach by the other party that remains uncured after a reasonable cure period, by notice,
            with effect from the moment the notice is deemed received.
          </p>
          <p style={pStyle}>
            A Partner that accepted version 1.1 at sign-up may terminate by notice deemed received before its
            first billing date, with effect from the moment the notice is deemed received, and owes no Fees.
          </p>
          <p style={pStyle}>
            On termination, the per-client fees for the final monthly period, on the count taken at its start, are billed
            in full, without proration, on the termination date. The base fee for the monthly period in progress is not
            refunded. An annual prepayment is not refunded, except that unused whole monthly periods are refunded pro rata
            when (a) the Partner terminates over a Material Update, under the update-exit paragraph of this Section (the
            paragraph beginning &ldquo;If a Partner does not accept a Material Update&rdquo;), (b) the Partner terminates
            for a material breach by BluePrint that BluePrint has not cured, or (c) BluePrint terminates for convenience,
            without cause.
          </p>
          <p style={pStyle}>
            On termination the Partner has <strong>30 days</strong> to export Client data in a portable format, after
            which BluePrint may permanently delete such data.
          </p>
          <p style={pStyle}>
            BluePrint may update these terms by publishing a new version and giving the Partner notice of it.
            The notice must list each change that is a Material Update and the date on which it applies. For an
            existing Partner, a new version takes effect 30 days after that notice is deemed received and
            applies from the first billing date at or after that time, so an update never changes the monthly
            period in progress. A Material Update that the notice does not list does not apply to the Partner
            until BluePrint gives a notice listing it and the date on which it applies; that notice is then the
            notice of update for that change, and this Section, including the update-exit deadline, applies to
            it from that notice. Changes to Fees or to the Pooled Token Allowance do not apply to a prepaid
            annual term until it renews, that is, until the first renewal billing date on or after the date the
            new version takes effect; until then the previous Fees and Pooled Token Allowance continue.
            Continued use of the Platform after a part of a new version applies is acceptance of that part only;
            a part that applies on a later date is not accepted by use before that date. New Partners accept the
            version current at sign-up. Until a new version, or a part of it, applies to a Partner, the version
            the Partner last accepted applies.
          </p>
          <p style={pStyle}>
            BluePrint may suspend the Partner&apos;s access to the Platform only: (a) if Fees remain unpaid 15
            days after they fall due; (b) to the extent reasonably necessary to address a security risk to the
            Platform or its other users; or (c) if the Partner materially breaches these terms. Before
            suspending under (a) or (c), BluePrint will give notice stating the reason, and will suspend only if
            the cause is not resolved within 7 days of that notice. Before suspending under (b), BluePrint will
            give notice stating the reason and whether the Partner caused the risk, except where the security
            risk requires immediate action, in which case BluePrint may suspend without prior notice and will
            give that notice promptly afterwards. Fees continue during a suspension under (a) or (c). For a
            suspension under (b) that the notice states the Partner did not cause, BluePrint will credit the
            Fees for the suspended period, pro rata by day: each day is credited at the Fees for that monthly
            period divided by the number of days in it, taking the base fee of a Partner on annual prepayment as
            the annual base fee divided by 12. The credit is applied to the next invoice or, if the agreement
            has terminated, paid to the Partner on the termination date, and is not limited by the refund rules
            above. BluePrint will restore access promptly once the cause of the suspension is resolved.
            Suspension does not end this agreement.
          </p>
          <p style={pStyle}>
            If a Partner does not accept a Material Update, it may terminate by notice deemed received no later
            than 23:59:59 UTC on the day before the update first applies; termination is then effective at
            23:59:59 UTC on that day, and until then the previous version continues to govern every part of the
            update that has not yet applied. An update that is not a Material Update gives no right to terminate
            under this paragraph. The move from version 1.0 to version 1.1 is a Material Update for a Partner
            active under version 1.0. Where parts of an update apply on different dates, each part keeps its own
            deadline. For a Material Update that changes the Fees or the Pooled Token Allowance and applies to a
            prepaid annual term only on its renewal (the first renewal billing date on or after the date the
            update takes effect), that change&apos;s deadline is the day before the renewal billing date: the
            Partner may terminate by notice deemed received no later than 23:59:59 UTC on the day before that
            renewal billing date, and termination is then effective at 23:59:59 UTC on that day, so that no
            unused monthly period remains to refund.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>11. Governing Law</h2>
          <p style={pStyle}>
            This agreement is governed by the laws of India.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>12. Commercial Terms Schedule</h2>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Item</th>
                <th style={thStyle}>Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>Base fee</td>
                <td style={tdStyle}>$199 / month</td>
              </tr>
              <tr>
                <td style={tdStyle}>Per-client fee</td>
                <td style={tdStyle}>$39 / month per Active Client Tenant</td>
              </tr>
              <tr>
                <td style={tdStyle}>Annual prepayment</td>
                <td style={tdStyle}>
                  20% discount on both fees: base fee billed annually in advance; per-client fees billed monthly in arrears
                </td>
              </tr>
              <tr>
                <td style={tdStyle}>Pooled Token Allowance</td>
                <td style={tdStyle}>20M + 5M per Active Client Tenant / month (pooled)</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>Revision history</h2>
          <ul style={ulStyle}>
            <li>
              <strong>Version 1.1 &mdash; October 2026.</strong>  Defines BluePrint as Arivaartham Labs Private
              Limited, and defines the Partner&apos;s administrators, notice (an email, deemed received 24 hours
              after it is sent, or after it is sent to the last of several addresses; BluePrint gives notice to
              each of the Partner&apos;s administrators or, if there is none, to the user who completed
              onboarding, and a Partner&apos;s notices go to support@blueprintsdlc.com), linked, AI actions,
              Active Client Tenant, billing date, monthly period, termination date, Material Update (an update
              that adversely affects the Partner, including a reduction of BluePrint&apos;s obligations or of
              the Partner&apos;s rights, but not a change in its favour or a rewording) and active under version
              1.0 (a Partner that accepted version 1.0 and has not terminated), and defines the Effective Date
              as the moment, in UTC, at which the Partner completes onboarding in the onboarding wizard (Section
              1). Section 2 refers to Client Tenants linked to the Partner&apos;s tenant, and allows BluePrint
              to revoke the licence only on termination under Section 10 and to suspend it only as Section 10
              allows. Billing dates are 00:00 UTC on the 1st of each calendar month, the first being the first
              one after the Effective Date or, for a Partner already active under version 1.0 when version 1.1
              applies to it, the first on or after the day version 1.1 applies to it; an annual term is 12
              monthly periods; and the part-month before the first billing date is free, with the base 20M-token
              Pooled Token Allowance only, except that a Partner already active under version 1.0 has no free
              part-month (Sections 1 and 3). Section 3 now opens: Fees are billed monthly, except that a Partner
              on annual prepayment is billed the base fee annually in advance; the base fee is billed in
              advance, on each billing date or, on annual prepayment, on the first billing date of each annual
              term. Moves the per-client fee from billing in advance to billing in arrears (Section 3). The
              Active Client Tenants counted at each billing date set that monthly period&apos;s per-client fee
              and Pooled Token Allowance (Sections 1 and 3). Annual prepayment now prepays the base fee only,
              and per-client fees stay billed monthly in arrears, both at the 20% discount (Sections 3 and 12).
              A Partner chooses annual prepayment, or switches to it, by notice to BluePrint, effective on the
              first billing date after the notice is deemed received, and moves from annual prepayment to
              monthly billing only at renewal; an annual term renews for another 12 monthly periods unless the
              Partner switches to monthly billing by notice deemed received at least 30 days before the renewal
              billing date, and an annual term chosen at sign-up starts on the first billing date (Section 3).
              Renames the allowance to the Pooled Token Allowance, shared across the Partner&apos;s Client
              Tenants rather than all of its tenants (Section 1), and sets it to 20M tokens per month plus 5M
              tokens per month for each Active Client Tenant. Removes the per-1,000-token rate for usage beyond
              the Pooled Token Allowance, and states that when the Pooled Token Allowance is used up, AI actions
              pause for all Client Tenants until the next monthly period (Section 3). In Section 12, the
              per-client fee row is per Active Client Tenant, the annual discount row becomes Annual prepayment,
              the allowance row becomes Pooled Token Allowance, and the per-1,000-token rate row is removed.
              Writes data after Client, and confidential information, in lower case (Sections 5, 6, 7 and 10),
              and calls BluePrint&apos;s own personnel BluePrint staff (Section 5). Adds how these terms are
              updated, in place of the provisional-status notice&apos;s agreement to the terms as updated from
              time to time: the notice of a new version lists each change that is a Material Update and the date
              it applies, and a Material Update it does not list applies only from a later notice listing it,
              which is then its notice of update; a new version takes effect 30 days after the notice is deemed
              received and applies from the first billing date at or after that time, never changing the monthly
              period in progress; changes to the Fees or to the Pooled Token Allowance wait, for a prepaid
              annual term, until the first renewal billing date on or after that time, and the previous Fees and
              allowance continue until then; continued use is acceptance of a part only once that part applies,
              and a part that applies later is not accepted by earlier use; a Partner that does not accept a
              Material Update may terminate by notice deemed received no later than 23:59:59 UTC on the day
              before it first applies, effective at 23:59:59 UTC that day, with the previous version governing
              every part not yet applied, while an update that is not a Material Update gives no such exit; the
              move from version 1.0 to version 1.1 is a Material Update for a Partner active under version 1.0;
              where parts of an update apply on different dates, each part keeps its own deadline, and for a
              Material Update to the Fees or the Pooled Token Allowance of a prepaid annual term the deadline is
              the day before the renewal billing date (Section 10). Adds a suspension clause: BluePrint may
              suspend access only for Fees unpaid 15 days after they fall due, a security risk, or the
              Partner&apos;s material breach, with notice stating the reason, and 7 days to resolve, except that
              a security suspension needs notice before it unless the risk requires immediate action, with Fees
              continuing except for a security suspension that BluePrint&apos;s notice states the Partner did
              not cause, which is credited by day (that monthly period&apos;s Fees divided by its days, the
              annual base fee divided by 12 on annual prepayment) on the next invoice or paid out on
              termination, and with prompt restoration (Section 10). Termination for convenience is by notice as
              defined in Section 1, effective at 23:59:59 UTC on the 30th day after the notice is deemed
              received; a Partner that accepted version 1.1 at sign-up may terminate by notice deemed received
              before its first billing date, with effect from that moment, owing no Fees; and termination for an
              uncured material breach is by notice, effective when it is deemed received (Section 10). On
              termination, the per-client fees for the final monthly period are billed in full, without
              proration, on the termination date, with no refund except unused whole monthly periods of an
              annual prepayment, pro rata, on a termination over a Material Update under the update-exit
              paragraph, a termination for BluePrint&apos;s uncured material breach, or a termination by
              BluePrint for convenience (Section 10). Names the contracting entity in the footer, writes Fees
              with a capital wherever the defined term is meant, and uses annual prepayment as the one name for
              paying a year ahead. Removes the governing-law caveat from Section 11. Removes the
              provisional-status notice.
            </li>
            <li><strong>Version 1.0 &mdash; April 2026.</strong> First version.</li>
          </ul>
        </section>

        <footer
          style={{
            marginTop: 32,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          &copy; Arivaartham Labs Private Limited. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
